import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { STICKY_CHAT_SIDE_CHAT_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the sticky-bar side-chat gate (`/api/flags/sticky-chat-side-chat`, Conductor experiment
 * `symphonyStickyChatSideChatWeb`): whether a tap on the wide "Ask {maestro}" bar opens the side chat, or
 * grows the bar into the inline composer as before.
 *
 * Fails CLOSED to the inline composer. A dev-tools Feature toggles override wins over the server. Memoized
 * per page load; only a real answer is remembered, so one failed read is asked again.
 */
const GATE_TIMEOUT_MS = 2500;

let cached: Promise<boolean> | null = null;
let known: boolean | null = null;

export function stickyChatSideChatEnabled(): Promise<boolean> {
  const forced = toggleOverride(STICKY_CHAT_SIDE_CHAT_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/sticky-chat-side-chat`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) return (known = ((await res.json()) as boolean) === true);
    } catch {
      // closed for now; asked again by the next reader
    }
    cached = null;
    return false;
  })();
  return cached;
}

export function useStickyChatSideChatEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => {
    const forced = toggleOverride(STICKY_CHAT_SIDE_CHAT_SPEC);
    return forced !== undefined ? forced === 'true' : (known ?? false);
  });
  useEffect(() => {
    let live = true;
    void stickyChatSideChatEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
